import { useQuery, useQueryClient, type QueryFunctionContext } from "@tanstack/react-query";
import { useEffect } from "react";
import { configuration } from "src/configuration";
import { useSignalR } from "src/hooks";
import type { FormSubmissionDto } from "src/http";
import { FetchError, UrlUtilities, useAccessToken } from "src/http";
import { queryKeys } from "../queryKeys";
import type { QueryParams } from "../utilities";
import { SignalRGroup } from "src/components";

const useGetFormSubmissions = (queryParams?: QueryParams) => {
  const { getAccessToken } = useAccessToken();
  const signalR = useSignalR();
  const queryClient = useQueryClient();

  async function getFormSubmissionsRequest({
    queryKey,
  }: QueryFunctionContext<ReturnType<typeof queryKeys.formSubmissions.getFormSubmissionsWithQueryParams>>): Promise<
    FormSubmissionDto[]
  > {
    const accessToken = await getAccessToken();
    const [{ queryParams }] = queryKey;

    const url = new URL(`${configuration.apiRootUrl}/form-submissions`);
    UrlUtilities.appendQueryParams(url, queryParams);

    const response = await fetch(url.href, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new FetchError(response);
    }

    return response.json();
  }

  useEffect(() => {
    const handler = (formSubmissionGuid: string, updates: Partial<FormSubmissionDto>) => {
      queryClient.setQueriesData<FormSubmissionDto[]>(queryKeys.formSubmissions.getFormSubmissions(), (cachedData) => {
        if (cachedData?.some((x) => x.formSubmissionGuid === formSubmissionGuid)) {
          return cachedData.map((formSubmission) => {
            if (formSubmission.formSubmissionGuid === formSubmissionGuid) {
              return {
                ...formSubmission,
                ...updates,
              };
            } else {
              return formSubmission;
            }
          });
        }

        return cachedData;
      });
    };

    signalR.joinGroup(SignalRGroup.FormSubmissions());
    signalR.onShouldUpdateFormSubmissions(handler);

    return () => {
      signalR.leaveGroup(SignalRGroup.FormSubmissions());
      signalR.offShouldUpdateFormSubmissions(handler);
    };
  }, [queryClient, signalR]);

  return useQuery({
    queryKey: queryKeys.formSubmissions.getFormSubmissionsWithQueryParams(queryParams),
    queryFn: getFormSubmissionsRequest,
  });
};

export { useGetFormSubmissions };
